/**
 * FRW Sizing Badges — Collection + Cart size recommendations.
 * Reads localStorage profile (from Perfect Fit quiz/camera) and product sizing
 * metafield data (embedded as JSON by Liquid snippets) to show:
 *   - Collection pages: "Your size: Medium" badge on product cards
 *   - Cart page: "Recommended for you" or mismatch warning on line items
 *
 * IIFE pattern. No build step. No imports.
 */
(function () {
  'use strict';

  var KEY = 'frw_perfect_fit';
  var EXPIRY_DAYS = 90;

  // Module-level sizing map — persists across calls so infinite scroll pages can merge in
  var sizingMap = {};

  // Find Your Fit — filter state
  var FYF_FILTER_KEY = 'frw_fit_filter';
  var FYF_QUICK_SIZE_KEY = 'frw_quick_size';
  var fyfFilterEnabled = false;
  var computedSizes = {}; // cache: productId → canonical size key

  // =========================================================================
  // Size chart lookup tables (duplicated from perfect-fit.js for independence)
  // =========================================================================
  var CHARTS = {
    womens: {
      XS:  { bust: [28, 30], waist: [24, 26], hip: [32, 34] },
      S:   { bust: [31, 33], waist: [27, 29], hip: [35, 37] },
      M:   { bust: [33, 36], waist: [30, 32], hip: [38, 40] },
      L:   { bust: [37, 39], waist: [33, 35], hip: [41, 43] },
      XL:  { bust: [40, 42], waist: [36, 38], hip: [44, 46] },
      XXL: { bust: [43, 45], waist: [39, 41], hip: [47, 49] },
      '1X': { bust: [46, 48], waist: [42, 44], hip: [50, 53] },
      '2X': { bust: [49, 52], waist: [45, 48], hip: [54, 58] }
    },
    mens_jersey: {
      S:   { chest: [48, 48] },
      M:   { chest: [50, 50] },
      L:   { chest: [52, 52] },
      XL:  { chest: [54, 54] },
      XXL: { chest: [56, 56] },
      '3XL': { chest: [58, 58] },
      '4XL': { chest: [60, 60] }
    },
    mens_tee: {
      S:   { chest: [38, 40] },
      M:   { chest: [40, 42] },
      L:   { chest: [42, 44] },
      XL:  { chest: [44, 46] },
      XXL: { chest: [46, 48] }
    },
    oversized_tee: {
      S:   { chest: [38, 40] },
      M:   { chest: [42, 44] },
      L:   { chest: [46, 48] },
      XL:  { chest: [50, 52] },
      XXL: { chest: [54, 56] }
    }
  };

  var SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '1X', '2X', '3XL', '4XL'];

  var SIZE_LABELS = {
    'XS': ['X-Small', 'XS'],
    'S': ['Small', 'S'],
    'M': ['Medium', 'M'],
    'L': ['Large', 'L'],
    'XL': ['X-Large', 'XL'],
    'XXL': ['XXL', '2XL'],
    '1X': ['1X'],
    '2X': ['2X'],
    '3XL': ['3XL'],
    '4XL': ['4XL']
  };

  var DISPLAY_MAP = {
    XS: 'X-Small', S: 'Small', M: 'Medium', L: 'Large', XL: 'X-Large'
  };

  // =========================================================================
  // Matching engine (duplicated from perfect-fit.js)
  // =========================================================================
  function fitScore(value, range) {
    var min = range[0], max = range[1];
    if (value >= min && value <= max) return 1.0;
    var dist = value < min ? min - value : value - max;
    return Math.max(0, 1.0 - (dist / (max - min + 2)));
  }

  function matchSize(measurements, chartKey, fitDirection, stretch, fitPref) {
    var chart = CHARTS[chartKey] || CHARTS.womens;
    var isWomens = chartKey === 'womens';
    var scores = {};

    for (var size in chart) {
      var r = chart[size];
      var score = 0, total = 0;
      if (isWomens) {
        if (r.bust)  { score += fitScore(measurements.bust, r.bust) * 30; total += 30; }
        if (r.waist) { score += fitScore(measurements.waist, r.waist) * 35; total += 35; }
        if (r.hip)   { score += fitScore(measurements.hip, r.hip) * 35; total += 35; }
      } else {
        if (r.chest) { score += fitScore(measurements.bust, r.chest) * 100; total += 100; }
      }
      scores[size] = total > 0 ? score / total : 0;
    }

    var bestSize = null, bestScore = -1;
    for (var s in scores) {
      if (scores[s] > bestScore) { bestScore = scores[s]; bestSize = s; }
    }

    if (fitDirection === 'size_up') bestSize = sizeShift(bestSize, chart, 1);
    else if (fitDirection === 'leans_small' && fitPref !== 'snug') bestSize = sizeShift(bestSize, chart, 1);
    if (stretch <= 2 && fitDirection !== 'size_up') bestSize = sizeShift(bestSize, chart, 1);
    if (fitPref === 'snug') bestSize = sizeShift(bestSize, chart, -1) || bestSize;
    if (fitPref === 'roomy') bestSize = sizeShift(bestSize, chart, 1) || bestSize;

    return bestSize;
  }

  function sizeShift(current, chart, direction) {
    var keys = [];
    for (var k in chart) keys.push(k);
    keys.sort(function (a, b) {
      return SIZE_ORDER.indexOf(a) - SIZE_ORDER.indexOf(b);
    });
    var idx = keys.indexOf(current);
    if (idx === -1) return current;
    var newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= keys.length) return current;
    return keys[newIdx];
  }

  // =========================================================================
  // Profile + Analytics
  // =========================================================================
  function getProfile() {
    try {
      var stored = localStorage.getItem(KEY);
      if (!stored) return null;
      var data = JSON.parse(stored);
      if (Date.now() - (data.ts || 0) > EXPIRY_DAYS * 86400000) return null;
      return data;
    } catch (e) { return null; }
  }

  function track(event, data) {
    if (window.dataLayer) window.dataLayer.push(Object.assign({ event: event }, data || {}));
  }

  // =========================================================================
  // Collection badges
  // =========================================================================
  function initCollectionBadges() {
    var profile = getProfile();
    var quickSize = getQuickSize();
    if ((!profile || !profile.m) && !quickSize) return;

    // On first run, seed from the embedded JSON element
    var dataEl = document.getElementById('frw-sizing-data');
    if (dataEl) {
      try {
        var parsed = JSON.parse(dataEl.textContent);
        for (var key in parsed) sizingMap[key] = parsed[key];
      } catch (e) { /* ignore parse errors */ }
    }

    if (!Object.keys(sizingMap).length) return;

    var cards = document.querySelectorAll('product-card[data-product-id]');
    var badgeCount = 0;

    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var pid = card.getAttribute('data-product-id');
      var sizing = sizingMap[pid];
      if (!sizing) continue;

      // Quick pick overrides quiz profile
      var size;
      if (quickSize) {
        size = quickSize;
      } else {
        size = matchSize(profile.m, sizing.c, sizing.r, sizing.s, profile.f || 'tts');
      }
      if (!size) continue;

      // Cache computed size for FYF filter
      computedSizes[pid] = size;

      var displaySize = DISPLAY_MAP[size] || size;

      // Find the content group (title + price area)
      var contentEl = card.querySelector('.product-card__content');
      if (!contentEl) continue;

      var badgeHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg> Your size: ' + displaySize;

      // Update existing badge or create new one
      var existingBadge = card.querySelector('.pf-card-badge');
      if (existingBadge) {
        existingBadge.innerHTML = badgeHTML;
        continue;
      }

      var badge = document.createElement('div');
      badge.className = 'pf-card-badge';
      badge.innerHTML = badgeHTML;

      // Insert right after the price element for proximity
      var priceEl = contentEl.querySelector('product-price');
      if (priceEl) {
        priceEl.insertAdjacentElement('afterend', badge);
      } else {
        contentEl.appendChild(badge);
      }
      badgeCount++;
    }

    if (badgeCount > 0) {
      track('frw_sizing_badge_shown', { context: 'collection', count: badgeCount });
    }
  }

  // =========================================================================
  // Infinite scroll observer — re-run badges when new product cards appear
  // =========================================================================
  function observeGrid() {
    var grid = document.querySelector('[ref="grid"]');
    if (!grid) return;

    var observer = new MutationObserver(function (mutations) {
      var hasNewCards = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length > 0) { hasNewCards = true; break; }
      }
      if (hasNewCards) {
        initCollectionBadges();
        if (fyfFilterEnabled) applyFitFilter(true);
      }
    });

    observer.observe(grid, { childList: true });
  }

  // =========================================================================
  // Cart confirmation badges
  // =========================================================================
  function initCartBadges() {
    var profile = getProfile();
    if (!profile || !profile.m) return;

    var dataEl = document.getElementById('frw-cart-sizing-data');
    if (!dataEl) return;

    var sizingMap;
    try { sizingMap = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var rows = document.querySelectorAll('.cart-items__table-row[data-key]');
    var badgeCount = 0;

    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var key = row.getAttribute('data-key');
      var info = sizingMap[key];
      if (!info) continue;

      var recSize = matchSize(profile.m, info.c, info.r, info.s, profile.f || 'tts');
      if (!recSize) continue;

      var details = row.querySelector('.cart-items__details');
      if (!details) continue;

      // Avoid duplicates
      if (details.querySelector('.pf-cart-badge')) continue;

      // Extract selected size from variant options
      var selectedSize = null;
      var dds = details.querySelectorAll('.cart-items__variants dd, .cart-items__variant dd');
      for (var j = 0; j < dds.length; j++) {
        var val = dds[j].textContent.trim();
        for (var s in SIZE_LABELS) {
          var labels = SIZE_LABELS[s];
          for (var k = 0; k < labels.length; k++) {
            if (val === labels[k]) { selectedSize = s; break; }
          }
          if (selectedSize) break;
        }
        if (selectedSize) break;
      }

      var el = document.createElement('div');
      el.className = 'pf-cart-badge';

      if (selectedSize === recSize) {
        el.innerHTML = '<span class="pf-cart-match">\u2713 Recommended for you</span>';
      } else if (selectedSize) {
        var displayRec = DISPLAY_MAP[recSize] || recSize;
        el.innerHTML = '<span class="pf-cart-mismatch">Your recommended size is ' + displayRec + '</span>';
      }

      if (el.innerHTML) {
        details.appendChild(el);
        badgeCount++;
      }
    }

    if (badgeCount > 0) {
      track('frw_sizing_badge_shown', { context: 'cart', count: badgeCount });
    }
  }

  // =========================================================================
  // Find Your Fit — collection filter bar
  // =========================================================================
  function getQuickSize() {
    try {
      var stored = localStorage.getItem(FYF_QUICK_SIZE_KEY);
      if (!stored) return null;
      var data = JSON.parse(stored);
      if (Date.now() - (data.ts || 0) > EXPIRY_DAYS * 86400000) return null;
      return data.size || null;
    } catch (e) { return null; }
  }

  function getUserSize() {
    // Quick pick wins if set (user explicitly chose a size)
    var quick = getQuickSize();
    if (quick) return quick;
    // Otherwise compute from quiz/camera profile
    var profile = getProfile();
    if (profile && profile.m) {
      for (var pid in sizingMap) {
        var sizing = sizingMap[pid];
        return matchSize(profile.m, sizing.c, sizing.r, sizing.s, profile.f || 'tts');
      }
    }
    return null;
  }

  function seedSizingMap() {
    var dataEl = document.getElementById('frw-sizing-data');
    if (dataEl) {
      try {
        var parsed = JSON.parse(dataEl.textContent);
        for (var key in parsed) sizingMap[key] = parsed[key];
      } catch (e) { /* ignore */ }
    }
  }

  // =========================================================================
  // Mobile inline FYF — compact button in the filter toolbar
  // =========================================================================
  function createMobileInlineFyf() {
    if (window.innerWidth >= 750) return;

    var toolbar = document.querySelector('.facets-toggle');
    if (!toolbar) return;

    // Remove existing if re-initializing (e.g., after quiz completion)
    var existing = document.getElementById('fyf-inline-btn');
    if (existing) existing.remove();
    var existingPopover = document.getElementById('fyf-mobile-popover');
    if (existingPopover) existingPopover.remove();

    var userSize = getUserSize();
    var container = document.createElement('div');
    container.style.position = 'relative';
    container.style.display = 'inline-flex';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'fyf-inline';
    btn.id = 'fyf-inline-btn';

    var rulerSvg = '<svg class="fyf-inline__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18M3 3v18M21 3v18M3 3h18M3 12h3M3 7h2M3 17h2M12 3v3M7 3v2M17 3v2"/></svg>';
    var checkSvg = '<svg class="fyf-inline__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
    var chevronSvg = '<svg class="fyf-inline__chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>';

    if (userSize) {
      var displaySize = DISPLAY_MAP[userSize] || userSize;
      btn.innerHTML = checkSvg + '<span>' + displaySize + '</span>' + chevronSvg;
      btn.classList.add('fyf-inline--has-size');
      btn.setAttribute('aria-label', 'Your size: ' + displaySize + '. Tap to change or retake.');

      // Build popover with actions
      var popover = document.createElement('div');
      popover.id = 'fyf-mobile-popover';
      popover.className = 'pf-popover';

      popover.innerHTML =
        '<button class="pf-popover__item" data-action="retake">' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>' +
          'Retake body scan' +
        '</button>' +
        '<div class="pf-popover__divider"></div>' +
        '<button class="pf-popover__item" data-action="clear">' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
          'Clear my size' +
        '</button>';

      container.appendChild(popover);

      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var isOpen = popover.classList.contains('pf-popover--open');
        popover.classList.toggle('pf-popover--open', !isOpen);
        btn.classList.toggle('fyf-inline--open', !isOpen);
      });

      popover.addEventListener('click', function (e) {
        var item = e.target.closest('.pf-popover__item');
        if (!item) return;
        e.stopPropagation();
        var action = item.getAttribute('data-action');

        if (action === 'retake') {
          clearUserSize();
          popover.classList.remove('pf-popover--open');
          btn.classList.remove('fyf-inline--open');
          track('frw_sizing_retake', { method: 'mobile_pill' });
          // Try to open quiz/scan
          document.dispatchEvent(new CustomEvent('frw:open-perfect-fit'));
          // Also try clicking any quiz trigger on page
          var quizTrigger = document.querySelector('[data-perfect-fit], .perfect-fit-trigger, #perfect-fit-trigger, [href*="perfect-fit"], [href*="find-your-fit"]');
          if (quizTrigger) quizTrigger.click();
        }

        if (action === 'clear') {
          clearUserSize();
          track('frw_sizing_cleared', { method: 'mobile_pill' });
          // Strip size filter params from URL and reload
          var url = new URL(window.location.href);
          var params = url.searchParams;
          var toDelete = [];
          params.forEach(function (val, key) {
            if (key.toLowerCase().indexOf('size') !== -1) toDelete.push(key);
          });
          for (var d = 0; d < toDelete.length; d++) params.delete(toDelete[d]);
          window.location.href = url.toString();
        }
      });

      // Close popover on outside tap
      document.addEventListener('click', function () {
        popover.classList.remove('pf-popover--open');
        btn.classList.remove('fyf-inline--open');
      });

    } else {
      btn.innerHTML = rulerSvg + '<span>Find Size</span>';
      btn.setAttribute('aria-label', 'Find your perfect size');
      btn.addEventListener('click', function () {
        document.dispatchEvent(new CustomEvent('frw:open-perfect-fit'));
        track('frw_find_your_fit_quiz_opened', { source: 'mobile_inline' });
      });
    }

    container.appendChild(btn);

    var mobileWrapper = toolbar.querySelector('.facets-mobile-wrapper');
    if (mobileWrapper) {
      toolbar.insertBefore(container, mobileWrapper);
    } else {
      toolbar.appendChild(container);
    }
  }

  function initFindYourFit() {
    var fyfEl = document.getElementById('frw-find-your-fit');
    if (!fyfEl) return;

    // Ensure sizing map is populated
    if (!Object.keys(sizingMap).length) seedSizingMap();
    if (!Object.keys(sizingMap).length) return; // no sizing data at all

    var profile = getProfile();
    var quickSize = getQuickSize();
    var hasSize = (profile && profile.m) || quickSize;

    var noProfileEl = fyfEl.querySelector('[data-fyf-state="no-profile"]');
    var hasProfileEl = fyfEl.querySelector('[data-fyf-state="has-profile"]');

    // Bind all listeners once
    bindFyfListeners(fyfEl);

    if (hasSize) {
      showHasProfileState(fyfEl, noProfileEl, hasProfileEl);
    } else {
      showNoProfileState(fyfEl, noProfileEl, hasProfileEl);
    }

    // On mobile, inject compact FYF button into the filter toolbar
    createMobileInlineFyf();

    track('frw_find_your_fit_shown', { state: hasSize ? 'has_size' : 'no_size' });
  }

  function showNoProfileState(fyfEl, noProfileEl, hasProfileEl) {
    noProfileEl.style.display = '';
    hasProfileEl.style.display = 'none';

    var cta = fyfEl.querySelector('[data-fyf-action="open-picker"]');
    var picker = fyfEl.querySelector('[data-fyf-picker]');

    // Reset CTA visibility and hide picker when re-entering this state
    if (cta) { cta.style.display = ''; cta.setAttribute('aria-expanded', 'false'); }
    if (picker) picker.style.display = 'none';
  }

  function showHasProfileState(fyfEl, noProfileEl, hasProfileEl) {
    noProfileEl.style.display = 'none';
    hasProfileEl.style.display = '';

    var userSize = getUserSize();
    if (!userSize) {
      showNoProfileState(fyfEl, noProfileEl, hasProfileEl);
      return;
    }

    var displaySize = DISPLAY_MAP[userSize] || userSize;
    var sizeLabel = fyfEl.querySelector('[data-fyf-size]');
    if (sizeLabel) sizeLabel.textContent = 'Your Fit: ' + displaySize;

    // Read persisted toggle state
    var savedState = localStorage.getItem(FYF_FILTER_KEY) === 'true';
    var toggle = fyfEl.querySelector('[data-fyf-action="toggle-filter"]');

    if (savedState && toggle) {
      toggle.setAttribute('aria-checked', 'true');
      applyFitFilter(true);
    }
  }

  function bindFyfListeners(fyfEl) {
    var noProfileEl = fyfEl.querySelector('[data-fyf-state="no-profile"]');
    var hasProfileEl = fyfEl.querySelector('[data-fyf-state="has-profile"]');
    var cta = fyfEl.querySelector('[data-fyf-action="open-picker"]');
    var picker = fyfEl.querySelector('[data-fyf-picker]');
    var toggle = fyfEl.querySelector('[data-fyf-action="toggle-filter"]');
    var editBtn = fyfEl.querySelector('[data-fyf-action="edit-size"]');

    // CTA → open size picker
    if (cta) {
      cta.addEventListener('click', function () {
        cta.style.display = 'none';
        if (picker) picker.style.display = '';
        cta.setAttribute('aria-expanded', 'true');
        renderSizePills(fyfEl);
        track('frw_find_your_fit_picker_opened');
      });
    }

    // Toggle → filter products
    if (toggle) {
      toggle.addEventListener('click', function () {
        var isOn = toggle.getAttribute('aria-checked') === 'true';
        var newState = !isOn;
        toggle.setAttribute('aria-checked', String(newState));
        applyFitFilter(newState);
        track('frw_find_your_fit_toggle', { enabled: newState });
      });
    }

    // Change → reset to picker
    if (editBtn) {
      editBtn.addEventListener('click', function () {
        localStorage.removeItem(FYF_QUICK_SIZE_KEY);
        applyFitFilter(false);
        if (toggle) toggle.setAttribute('aria-checked', 'false');
        showNoProfileState(fyfEl, noProfileEl, hasProfileEl);
      });
    }

    // Quiz link → open Perfect Fit body scan/quiz modal
    var quizBtn = fyfEl.querySelector('[data-fyf-action="open-quiz"]');
    if (quizBtn) {
      quizBtn.addEventListener('click', function () {
        document.dispatchEvent(new CustomEvent('frw:open-perfect-fit'));
        track('frw_find_your_fit_quiz_opened');
      });
    }
  }

  function renderSizePills(fyfEl) {
    var pillsContainer = fyfEl.querySelector('.fyf__pills');
    if (!pillsContainer) return;

    // Determine dominant chart type from sizing data
    var chartCounts = {};
    for (var pid in sizingMap) {
      var c = sizingMap[pid].c || 'womens';
      chartCounts[c] = (chartCounts[c] || 0) + 1;
    }
    var dominantChart = 'womens';
    var maxCount = 0;
    for (var k in chartCounts) {
      if (chartCounts[k] > maxCount) { maxCount = chartCounts[k]; dominantChart = k; }
    }

    var chart = CHARTS[dominantChart];
    var sizes = [];
    for (var s in chart) sizes.push(s);
    sizes.sort(function (a, b) {
      return SIZE_ORDER.indexOf(a) - SIZE_ORDER.indexOf(b);
    });

    pillsContainer.innerHTML = '';
    for (var i = 0; i < sizes.length; i++) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'fyf__pill';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', 'false');
      btn.textContent = DISPLAY_MAP[sizes[i]] || sizes[i];
      btn.setAttribute('data-size', sizes[i]);
      btn.addEventListener('click', onPillClick);
      pillsContainer.appendChild(btn);
    }
  }

  function onPillClick(e) {
    var btn = e.currentTarget;
    var size = btn.getAttribute('data-size');
    var fyfEl = document.getElementById('frw-find-your-fit');

    // Update pill visual states
    var allPills = fyfEl.querySelectorAll('.fyf__pill');
    for (var i = 0; i < allPills.length; i++) {
      allPills[i].classList.remove('fyf__pill--active');
      allPills[i].setAttribute('aria-checked', 'false');
    }
    btn.classList.add('fyf__pill--active');
    btn.setAttribute('aria-checked', 'true');

    // Save quick size
    localStorage.setItem(FYF_QUICK_SIZE_KEY, JSON.stringify({ size: size, ts: Date.now() }));

    // Update card badges to reflect new size
    initCollectionBadges();

    // Transition to has-profile state
    var noProfileEl = fyfEl.querySelector('[data-fyf-state="no-profile"]');
    var hasProfileEl = fyfEl.querySelector('[data-fyf-state="has-profile"]');
    showHasProfileState(fyfEl, noProfileEl, hasProfileEl);

    track('frw_find_your_fit_quick_select', { size: size });
  }

  function applyFitFilter(enabled) {
    fyfFilterEnabled = enabled;
    localStorage.setItem(FYF_FILTER_KEY, enabled ? 'true' : 'false');

    var profile = getProfile();
    var quickSize = getQuickSize();
    var items = document.querySelectorAll('li.product-grid__item[data-product-id]');
    var totalCount = items.length;
    var visibleCount = 0;

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var pid = item.getAttribute('data-product-id');
      var sizing = sizingMap[pid];

      if (!enabled) {
        // Show everything
        item.classList.remove('fyf-hidden');
        visibleCount++;
        continue;
      }

      // No sizing data → keep visible
      if (!sizing || !sizing.av) {
        item.classList.remove('fyf-hidden');
        visibleCount++;
        continue;
      }

      // Compute recommended size for this product
      // Quick pick overrides quiz profile (user explicitly chose)
      var recSize;
      if (quickSize) {
        recSize = quickSize;
      } else if (profile && profile.m) {
        if (computedSizes[pid]) {
          recSize = computedSizes[pid];
        } else {
          recSize = matchSize(profile.m, sizing.c, sizing.r, sizing.s, profile.f || 'tts');
          if (recSize) computedSizes[pid] = recSize;
        }
      }

      if (!recSize) {
        item.classList.remove('fyf-hidden');
        visibleCount++;
        continue;
      }

      // Check availability for the recommended size
      if (sizing.av[recSize] === false) {
        item.classList.add('fyf-hidden');
      } else {
        item.classList.remove('fyf-hidden');
        visibleCount++;
      }
    }

    // Update count display
    var countEl = document.querySelector('[data-fyf-count]');
    if (countEl) {
      if (enabled && visibleCount < totalCount) {
        countEl.textContent = '(' + visibleCount + ' of ' + totalCount + ')';
      } else {
        countEl.textContent = '';
      }
    }
  }

  // =========================================================================
  // Filter bar — FYF + Budget as inline items
  // =========================================================================
  var BUDGET_TIERS = [
    { id: 1, label: 'Under $40', min: 0, max: 40 },
    { id: 2, label: '$40 – $75', min: 40, max: 75 },
    { id: 3, label: '$75 – $120', min: 75, max: 120 },
    { id: 4, label: '$120 – $200', min: 120, max: 200 },
    { id: 5, label: '$200+', min: 200, max: 0 }
  ];

  var CARET_SVG = '<span class="svg-wrapper icon-caret icon-animated"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';

  function injectFilterBarItems() {
    var forms = document.querySelectorAll('.facets__form');
    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];
      var isDrawer = !!form.closest('#filters-drawer');

      // My Size goes right after the Size filter (replace placeholder or inject)
      var sizeAccordion = form.querySelector('[data-filter-param-name*="size"]');
      var existingFyf = form.querySelector('#fyf-facet-item');
      if (sizeAccordion) {
        var newFyf = isDrawer ? buildDrawerFyfItem() : buildFyfItem();
        if (existingFyf && !existingFyf.dataset.hydrated) {
          // Hydrate in-place: keep placeholder DOM, add missing pieces without touching label text
          existingFyf.dataset.hydrated = '1';
          var hydratedBtn = existingFyf.querySelector('.fyf-facet-btn');
          var userSize = getUserSize();
          if (hydratedBtn && userSize) {
            // Add caret SVG if missing (inline script only set the label text)
            if (!hydratedBtn.querySelector('.icon-caret')) {
              var label = hydratedBtn.querySelector('.facets__label');
              if (label) label.insertAdjacentHTML('afterend', CARET_SVG);
            }
            hydratedBtn.setAttribute('aria-expanded', 'false');
            // Grab the panel from the built element and append it
            var panel = newFyf.querySelector('.fyf-budget-panel');
            if (panel) existingFyf.appendChild(panel);
            hydratedBtn.addEventListener('click', function (e) {
              e.stopPropagation();
              var openDetails = document.querySelectorAll('.facets--horizontal .facets__item details[open]');
              for (var j = 0; j < openDetails.length; j++) openDetails[j].removeAttribute('open');
              var bp = document.querySelector('.fyf-budget-panel--open');
              if (bp && bp !== panel) bp.classList.remove('fyf-budget-panel--open');
              var open = panel.classList.toggle('fyf-budget-panel--open');
              hydratedBtn.setAttribute('aria-expanded', String(open));
            });
            document.addEventListener('click', function () {
              panel.classList.remove('fyf-budget-panel--open');
              hydratedBtn.setAttribute('aria-expanded', 'false');
            });
          } else if (hydratedBtn) {
            hydratedBtn.addEventListener('click', function () {
              document.dispatchEvent(new CustomEvent('frw:open-perfect-fit'));
              track('frw_find_your_fit_quiz_opened', { source: 'filter_bar' });
            });
          }
        } else if (existingFyf) {
          existingFyf.replaceWith(newFyf);
        } else {
          sizeAccordion.after(newFyf);
        }
      }

      // Budget goes right after the Price filter (replace placeholder or inject)
      var priceAccordion = null;
      var priceFacet = form.querySelector('price-facet-component');
      if (priceFacet) priceAccordion = priceFacet.closest('.facets__item');
      var existingBudget = form.querySelector('#fyf-budget-item');
      if (priceAccordion) {
        var newBudget = isDrawer ? buildDrawerBudgetItem() : buildBudgetItem();
        if (existingBudget) { existingBudget.replaceWith(newBudget); } else { priceAccordion.after(newBudget); }
      }

      // Drawer: only Size open by default, collapse everything else
      if (isDrawer) {
        collapseDrawerFilters(form);
      }
    }
  }

  function collapseDrawerFilters(form) {
    var items = form.querySelectorAll('.facets__item');
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var isSizeFilter = item.getAttribute('data-filter-param-name') &&
        item.getAttribute('data-filter-param-name').indexOf('size') !== -1;
      if (!isSizeFilter) {
        var details = item.querySelector('details[open]');
        if (details) details.removeAttribute('open');
      }
    }
  }

  // ── Desktop horizontal builders ──

  function buildFyfItem() {
    var item = document.createElement('div');
    item.className = 'facets__item';
    item.id = 'fyf-facet-item';
    item.style.position = 'relative';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'fyf-facet-btn';

    var userSize = getUserSize();
    if (userSize) {
      var ds = DISPLAY_MAP[userSize] || userSize;
      btn.innerHTML = '<span class="facets__label">My Size \u00B7 ' + ds + '</span>' + CARET_SVG;
      btn.setAttribute('aria-expanded', 'false');

      var panel = document.createElement('div');
      panel.className = 'fyf-budget-panel';

      var retakeOpt = document.createElement('button');
      retakeOpt.type = 'button';
      retakeOpt.className = 'fyf-budget-option';
      retakeOpt.textContent = 'Retake Quiz';
      retakeOpt.addEventListener('click', function () {
        panel.classList.remove('fyf-budget-panel--open');
        btn.setAttribute('aria-expanded', 'false');
        document.dispatchEvent(new CustomEvent('frw:open-perfect-fit'));
        track('frw_find_your_fit_quiz_opened', { source: 'filter_bar_retake' });
      });

      var clearOpt = document.createElement('button');
      clearOpt.type = 'button';
      clearOpt.className = 'fyf-budget-option';
      clearOpt.textContent = 'Clear My Size';
      clearOpt.addEventListener('click', function () {
        clearUserSize();
        panel.classList.remove('fyf-budget-panel--open');
        btn.setAttribute('aria-expanded', 'false');
        window.location.reload();
      });

      panel.appendChild(retakeOpt);
      panel.appendChild(clearOpt);

      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var openDetails = document.querySelectorAll('.facets--horizontal .facets__item details[open]');
        for (var j = 0; j < openDetails.length; j++) openDetails[j].removeAttribute('open');
        // Close budget panel if open
        var budgetPanel = document.querySelector('.fyf-budget-panel--open');
        if (budgetPanel && budgetPanel !== panel) budgetPanel.classList.remove('fyf-budget-panel--open');
        var open = panel.classList.toggle('fyf-budget-panel--open');
        btn.setAttribute('aria-expanded', String(open));
      });

      document.addEventListener('click', function () {
        panel.classList.remove('fyf-budget-panel--open');
        btn.setAttribute('aria-expanded', 'false');
      });

      item.appendChild(btn);
      item.appendChild(panel);
    } else {
      btn.innerHTML = '<span class="facets__label">Find My Size</span>';
      btn.addEventListener('click', function () {
        document.dispatchEvent(new CustomEvent('frw:open-perfect-fit'));
        track('frw_find_your_fit_quiz_opened', { source: 'filter_bar' });
      });
      item.appendChild(btn);
    }

    return item;
  }

  function getActiveBudgetTier() {
    var url = new URL(window.location.href);
    var gte = url.searchParams.get('filter.v.price.gte');
    var lte = url.searchParams.get('filter.v.price.lte');
    if (!gte && !lte) return null;
    var min = gte ? parseFloat(gte) : 0;
    var max = lte ? parseFloat(lte) : 0;
    for (var i = 0; i < BUDGET_TIERS.length; i++) {
      var t = BUDGET_TIERS[i];
      if (t.min === min && t.max === max) return t;
    }
    return { id: 0, label: '$' + (min || 0) + ' – $' + (max || '∞'), min: min, max: max };
  }

  function clearBudgetUrl() {
    var url = new URL(window.location.href);
    url.searchParams.delete('filter.v.price.gte');
    url.searchParams.delete('filter.v.price.lte');
    window.location.href = url.toString();
  }

  function buildBudgetItem() {
    var item = document.createElement('div');
    item.className = 'facets__item';
    item.id = 'fyf-budget-item';
    item.style.position = 'relative';

    var activeTier = getActiveBudgetTier();

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'fyf-facet-btn';
    if (activeTier) {
      btn.classList.add('fyf-facet-btn--active');
      btn.innerHTML = '<span class="facets__label">Budget \u00B7 ' + activeTier.label + '</span>' + CARET_SVG;
    } else {
      btn.innerHTML = '<span class="facets__label">Budget</span>' + CARET_SVG;
    }
    btn.setAttribute('aria-expanded', 'false');

    var panel = document.createElement('div');
    panel.className = 'fyf-budget-panel';

    // Add "Clear" option if a budget is active
    if (activeTier) {
      var clearOpt = document.createElement('button');
      clearOpt.type = 'button';
      clearOpt.className = 'fyf-budget-option';
      clearOpt.textContent = 'All Prices';
      clearOpt.style.gridColumn = '1 / -1';
      clearOpt.style.borderColor = 'rgb(var(--color-foreground-rgb) / .3)';
      clearOpt.addEventListener('click', function () {
        clearBudgetUrl();
      });
      panel.appendChild(clearOpt);
    }

    for (var i = 0; i < BUDGET_TIERS.length; i++) {
      (function (tier) {
        var opt = document.createElement('button');
        opt.type = 'button';
        opt.className = 'fyf-budget-option';
        if (activeTier && activeTier.id === tier.id) {
          opt.style.borderColor = 'rgb(var(--color-foreground-rgb) / .4)';
          opt.style.background = 'rgb(var(--color-foreground-rgb) / .1)';
        }
        opt.textContent = tier.label;
        if (tier.note) {
          opt.innerHTML = tier.label + '<span class="fyf-budget-note">' + tier.note + '</span>';
        }
        opt.addEventListener('click', function () {
          if (activeTier && activeTier.id === tier.id) {
            clearBudgetUrl();
          } else {
            applyBudgetUrl(tier);
          }
          panel.classList.remove('fyf-budget-panel--open');
          btn.setAttribute('aria-expanded', 'false');
        });
        panel.appendChild(opt);
      })(BUDGET_TIERS[i]);
    }

    function closeBudget() {
      panel.classList.remove('fyf-budget-panel--open');
      btn.setAttribute('aria-expanded', 'false');
    }

    function closeNativeFilters() {
      var openDetails = document.querySelectorAll('.facets--horizontal .facets__item details[open]');
      for (var j = 0; j < openDetails.length; j++) {
        openDetails[j].removeAttribute('open');
      }
    }

    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      closeNativeFilters();
      var open = panel.classList.toggle('fyf-budget-panel--open');
      btn.setAttribute('aria-expanded', String(open));
    });

    document.addEventListener('click', function () {
      closeBudget();
    });

    item.appendChild(btn);
    item.appendChild(panel);
    return item;
  }

  // ── Mobile drawer builders — match native accordion pattern ──

  function clearUserSize() {
    try { localStorage.removeItem(KEY); } catch (e) {}
    try { localStorage.removeItem(FYF_QUICK_SIZE_KEY); } catch (e) {}
    computedSizes = {};
  }

  function buildDrawerFyfItem() {
    var wrapper = document.createElement('accordion-custom');
    wrapper.className = 'facets__item';
    wrapper.id = 'fyf-facet-item';

    var details = document.createElement('details');
    details.className = 'facets__panel';

    var summary = document.createElement('summary');
    summary.className = 'facets__summary';

    var userSize = getUserSize();
    var label = userSize ? 'My Size \u00B7 ' + (DISPLAY_MAP[userSize] || userSize) : 'Find My Size';
    summary.innerHTML = '<span class="facets__label">' + label + '</span>' +
      '<div class="facets__status-wrapper">' + CARET_SVG + '</div>';

    details.appendChild(summary);

    // If no profile, clicking opens quiz directly
    if (!userSize) {
      summary.addEventListener('click', function (e) {
        e.preventDefault();
        document.dispatchEvent(new CustomEvent('frw:open-perfect-fit'));
        track('frw_find_your_fit_quiz_opened', { source: 'filter_drawer' });
      });
    } else {
      // Has profile — expand to show retake + clear options
      var content = document.createElement('div');
      content.className = 'fyf-drawer-fyf-options';

      var retakeBtn = document.createElement('button');
      retakeBtn.type = 'button';
      retakeBtn.className = 'fyf-drawer-budget-opt';
      retakeBtn.textContent = 'Retake Quiz';
      retakeBtn.addEventListener('click', function () {
        document.dispatchEvent(new CustomEvent('frw:open-perfect-fit'));
        track('frw_find_your_fit_quiz_opened', { source: 'filter_drawer_retake' });
      });

      var clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      clearBtn.className = 'fyf-drawer-budget-opt';
      clearBtn.textContent = 'Clear My Size';
      clearBtn.addEventListener('click', function () {
        clearUserSize();
        details.removeAttribute('open');
        window.location.reload();
      });

      content.appendChild(retakeBtn);
      content.appendChild(clearBtn);
      details.appendChild(content);
    }

    wrapper.appendChild(details);
    return wrapper;
  }

  function buildDrawerBudgetItem() {
    var wrapper = document.createElement('accordion-custom');
    wrapper.className = 'facets__item';
    wrapper.id = 'fyf-budget-item';

    var activeTier = getActiveBudgetTier();

    var details = document.createElement('details');
    details.className = 'facets__panel';

    var summary = document.createElement('summary');
    summary.className = 'facets__summary';
    var summaryLabel = activeTier ? 'Budget \u00B7 ' + activeTier.label : 'Budget';
    summary.innerHTML = '<span class="facets__label">' + summaryLabel + '</span>' +
      '<div class="facets__status-wrapper">' + CARET_SVG + '</div>';

    var content = document.createElement('div');
    content.className = 'fyf-drawer-budget-options';

    // Add "Clear" option if a budget is active
    if (activeTier) {
      var clearOpt = document.createElement('button');
      clearOpt.type = 'button';
      clearOpt.className = 'fyf-drawer-budget-opt';
      clearOpt.textContent = 'All Prices';
      clearOpt.style.gridColumn = '1 / -1';
      clearOpt.addEventListener('click', function () {
        clearBudgetUrl();
      });
      content.appendChild(clearOpt);
    }

    for (var i = 0; i < BUDGET_TIERS.length; i++) {
      (function (tier) {
        var opt = document.createElement('button');
        opt.type = 'button';
        opt.className = 'fyf-drawer-budget-opt';
        if (activeTier && activeTier.id === tier.id) {
          opt.style.borderColor = 'rgb(var(--color-foreground-rgb) / .4)';
          opt.style.background = 'rgb(var(--color-foreground-rgb) / .1)';
        }
        opt.textContent = tier.label;
        if (tier.note) {
          opt.innerHTML = tier.label + '<span class="fyf-budget-note">' + tier.note + '</span>';
        }
        opt.addEventListener('click', function () {
          if (activeTier && activeTier.id === tier.id) {
            clearBudgetUrl();
          } else {
            applyBudgetUrl(tier);
          }
        });
        content.appendChild(opt);
      })(BUDGET_TIERS[i]);
    }

    details.appendChild(summary);
    details.appendChild(content);
    wrapper.appendChild(details);
    return wrapper;
  }

  function applyBudgetUrl(tier) {
    var url = new URL(window.location.href);
    if (tier.min > 0) {
      url.searchParams.set('filter.v.price.gte', String(tier.min));
    } else {
      url.searchParams.delete('filter.v.price.gte');
    }
    if (tier.max > 0) {
      url.searchParams.set('filter.v.price.lte', String(tier.max));
    } else {
      url.searchParams.delete('filter.v.price.lte');
    }
    window.location.href = url.toString();
  }

  function watchFilterMorph() {
    // Re-inject FYF + Budget items whenever the filter section morphs.
    // The morph can be triggered by filter changes, clear-all, or URL nav.
    // It replaces DOM nodes unpredictably, so we use a broad MutationObserver
    // on document.body and check if our items survived.
    var reinjectTimer = null;

    function ensureItems() {
      if (reinjectTimer) return;
      if (!document.getElementById('fyf-facet-item') || !document.getElementById('fyf-budget-item')) {
        reinjectTimer = requestAnimationFrame(function () {
          reinjectTimer = null;
          injectFilterBarItems();
        });
      }
    }

    // MutationObserver on body — catches all morphs regardless of depth
    var observer = new MutationObserver(ensureItems);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // =========================================================================
  // Boot
  // =========================================================================
  function init() {
    if (document.getElementById('frw-sizing-data')) {
      initCollectionBadges();
      initFindYourFit();
      injectFilterBarItems();
      watchFilterMorph();
      observeGrid();

      // Re-init when user completes Perfect Fit quiz
      document.addEventListener('frw:perfect-fit-complete', function () {
        initCollectionBadges();
        initFindYourFit();
        injectFilterBarItems();
      });
    }
    if (document.getElementById('frw-cart-sizing-data')) initCartBadges();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
